import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
    CheckCircleIcon,
    XCircleIcon,
    ChevronDownIcon,
    CurrencyEuroIcon,
} from '@heroicons/react/24/outline';
import { Layout } from '@components';
import { SECTION_ITEMS, SectionItem } from '@data/configuratorSections';
import { ADMIN_SECTION_GROUPS, mirrorVestCategories, mirrorVestItems, mirrorVestValues } from '@data/vestSettings';
import { SECTION_LABELS, ITEM_LABELS } from '@data/configuratorLabels';
import {
    AdminPricing,
    AdminLiasse,
    getAdminSettings,
    updateAdminPricing,
    getAllAdminLiasses,
} from '@services/admin.service';
import { getButtonCatalog, ButtonCategory } from '@services/buttons.service';
import { getLiningCatalog, LiningCategory } from '@services/linings.service';
import { getCollarFeltCatalog, CollarFeltCategory } from '@services/collarFelts.service';
import { getContrastColorCatalog, ContrastColorOption } from '@services/contrastColors.service';

const DEFAULT_PRICING: AdminPricing = {
    showPrices: false,
    currency: 'EUR',
    dreamBoxPrice: 29,
    sections: {},
    items: {},
    buttonCategories: {},
    fabricGroups: {},
    fabrics: {},
};

const parsePrice = (raw: string): number | undefined => {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const normalized = trimmed.replace(',', '.');
    const num = Number(normalized);
    if (!Number.isFinite(num) || num < 0) return undefined;
    return num;
};

const formatPrice = (price: number | undefined): string =>
    price === undefined ? '' : String(price);

const labelSection = (key?: string, fallback?: string) => (key ? SECTION_LABELS[key] || fallback || key : fallback || '');
const labelItem = (key?: string, fallback?: string) => (key ? ITEM_LABELS[key] || fallback || key : fallback || '');

/** Prix de base indépendants des trois produits du panier. */
const PRODUCT_BASE_PRICES = [
    { key: 'product.suit', label: 'Costume', description: 'Costume 2 ou 3 pièces' },
    { key: 'product.shirt', label: 'Chemise', description: 'Chemise vendue seule' },
    { key: 'product.vest', label: 'Gilet', description: 'Gilet vendu seul (distinct du 3 pièces)' },
] as const;
const PRODUCT_BASE_PRICE_KEYS = new Set<string>(PRODUCT_BASE_PRICES.map((product) => product.key));
const SUIT_PRODUCT_KEY = 'product.suit';
const SUIT_TYPE_PRICING_KEY = 'coupe.suitType';
const BUTTON_SECTION_KEY = 'perso.buttons';
const PANTS_BUTTON_SECTION_KEY = 'perso.pantsButtons';
const VEST_BUTTON_SECTION_KEYS = ['perso.vestButtons', 'vest.buttons'];
const JACKET_LINING_SECTION_KEY = 'perso.jacketLining';
const PANTS_LINING_SECTION_KEY = 'perso.pantsLiningFabric';
const VEST_BACK_LINING_SECTION_KEYS = ['coupe.vestBackFabric', 'vest.backFabric'];
const LINING_SECTION_KEYS = [JACKET_LINING_SECTION_KEY, PANTS_LINING_SECTION_KEY, ...VEST_BACK_LINING_SECTION_KEYS];
const COLLAR_FELT_SECTION_KEY = 'perso.collarFelt';
const CONTRAST_SECTION_KEYS = ['perso.jacketContrast', 'perso.pantsContrast', 'perso.vestContrast', 'vest.contrast'];
type CatalogCategory = ButtonCategory | LiningCategory | CollarFeltCategory;
const isCatalogSection = (sectionKey: string): boolean =>
    [BUTTON_SECTION_KEY, PANTS_BUTTON_SECTION_KEY, ...VEST_BUTTON_SECTION_KEYS, ...LINING_SECTION_KEYS, COLLAR_FELT_SECTION_KEY, ...CONTRAST_SECTION_KEYS].includes(sectionKey);
const isButtonSection = (sectionKey: string): boolean =>
    sectionKey === BUTTON_SECTION_KEY || sectionKey === PANTS_BUTTON_SECTION_KEY || VEST_BUTTON_SECTION_KEYS.includes(sectionKey);
const buttonCategoryPriceKey = (sectionKey: string, categoryId: string): string =>
    sectionKey === BUTTON_SECTION_KEY ? categoryId : `${sectionKey}:${categoryId}`;
const BUTTON_CATEGORY_LABELS: Record<string, string> = {
    corozo: 'Corozo',
    metal: 'Métal',
    real_horn: 'Corne véritable',
    resin: 'Résine',
    shell: 'Nacre',
    shirt: 'Chemise',
};

const labelCatalogCategory = (category: CatalogCategory): string =>
    ('label' in category && category.label) || BUTTON_CATEGORY_LABELS[category.id] || category.id.replace(/_/g, ' ');

/** Reprend l'ancien prix global costume dans le nouveau prix de produit. */
const normalizeProductBasePricing = (pricing?: Partial<AdminPricing> | null): AdminPricing => {
    const merged: AdminPricing = {
        ...DEFAULT_PRICING,
        ...(pricing || {}),
        sections: { ...(pricing?.sections || {}) },
        items: { ...(pricing?.items || {}) },
        buttonCategories: { ...(pricing?.buttonCategories || {}) },
        fabricGroups: { ...(pricing?.fabricGroups || {}) },
        fabrics: { ...(pricing?.fabrics || {}) },
    };
    if (
        merged.sections[SUIT_PRODUCT_KEY] === undefined
        && merged.sections[SUIT_TYPE_PRICING_KEY] !== undefined
    ) {
        merged.sections[SUIT_PRODUCT_KEY] = merged.sections[SUIT_TYPE_PRICING_KEY];
    }
    // `coupe.suitType` reste réservé aux éventuels prix complets 2/3 pièces
    // stockés dans `items`. Conserver aussi son ancien prix de section créerait
    // une seconde source invisible dans l'interface et pourrait le réactiver
    // après suppression de `product.suit`.
    delete merged.sections[SUIT_TYPE_PRICING_KEY];
    return merged;
};

export const AdminPricingSettings: FC = () => {
    const [loading, setLoading] = useState(true);
    const [server, setServer] = useState<AdminPricing>(DEFAULT_PRICING);
    const [draft, setDraft] = useState<AdminPricing>(DEFAULT_PRICING);
    const [expandedSection, setExpandedSection] = useState<Record<string, boolean>>({});
    const [expandedButtonCategory, setExpandedButtonCategory] = useState<Record<string, boolean>>({});
    const [expandedLiasse, setExpandedLiasse] = useState<Record<string, boolean>>({});
    const [liasses, setLiasses] = useState<AdminLiasse[]>([]);
    const [liassesLoading, setLiassesLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [buttonCatalog, setButtonCatalog] = useState<ButtonCategory[]>([]);
    const [liningCatalog, setLiningCatalog] = useState<LiningCategory[]>([]);
    const [collarFeltCatalog, setCollarFeltCatalog] = useState<CollarFeltCategory[]>([]);
    const [contrastColorCatalog, setContrastColorCatalog] = useState<ContrastColorOption[]>([]);
    const contrastCategory = useMemo(() => [{ id: 'CONTRAST_COLORS', label: 'Couleurs de contraste', options: contrastColorCatalog }], [contrastColorCatalog]);

    const catalogForSection = useCallback((sectionKey: string): CatalogCategory[] =>
        CONTRAST_SECTION_KEYS.includes(sectionKey) ? contrastCategory
            : sectionKey === COLLAR_FELT_SECTION_KEY ? collarFeltCatalog
            : LINING_SECTION_KEYS.includes(sectionKey) ? liningCatalog : buttonCatalog,
    [buttonCatalog, liningCatalog, collarFeltCatalog, contrastCategory]);

    // Items par section, avec les boutons issus du catalogue backend (partagé front + admin).
    const sectionItems = useMemo<Record<string, SectionItem[]>>(() => {
        const buttons: SectionItem[] = [
            { id: 'none', labelKey: 'configurator.option.button.default' },
            { id: 'recouvert', label: 'Recouvert (tissu du costume)' },
            ...buttonCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
        ];
        return {
            ...SECTION_ITEMS,
            [BUTTON_SECTION_KEY]: buttons,
            [PANTS_BUTTON_SECTION_KEY]: buttons,
            [VEST_BUTTON_SECTION_KEYS[0]]: buttons,
            [VEST_BUTTON_SECTION_KEYS[1]]: buttons,
            [JACKET_LINING_SECTION_KEY]: [
                { id: 'match', label: 'Assortie au costume' },
                ...liningCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            [PANTS_LINING_SECTION_KEY]: [
                { id: 'match', label: 'Assortie au costume' },
                ...liningCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            [VEST_BACK_LINING_SECTION_KEYS[0]]: [
                ...(SECTION_ITEMS[VEST_BACK_LINING_SECTION_KEYS[0]] || []).filter((option) => option.id === 'front' || option.id === 'same'),
                ...liningCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            [VEST_BACK_LINING_SECTION_KEYS[1]]: [
                ...(SECTION_ITEMS[VEST_BACK_LINING_SECTION_KEYS[1]] || []).filter((option) => option.id === 'front'),
                ...liningCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            [COLLAR_FELT_SECTION_KEY]: [
                { id: 'match', label: 'Assorti au costume' },
                { id: 'other', label: 'Personnalisé' },
                ...collarFeltCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            ...Object.fromEntries(CONTRAST_SECTION_KEYS.map((key) => [key, [
                ...(SECTION_ITEMS[key] || []),
                ...contrastColorCatalog.map((option) => ({ id: option.id, label: option.name })),
            ]])),
        };
    }, [buttonCatalog, liningCatalog, collarFeltCatalog, contrastColorCatalog]);

    useEffect(() => {
        getButtonCatalog().then(setButtonCatalog).catch(() => { /* catalogue optionnel */ });
        getLiningCatalog().then(setLiningCatalog).catch(() => { /* catalogue optionnel */ });
        getCollarFeltCatalog().then(setCollarFeltCatalog).catch(() => { /* catalogue optionnel */ });
        getContrastColorCatalog().then(setContrastColorCatalog).catch(() => { /* catalogue optionnel */ });
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getAdminSettings();
                if (cancelled) return;
                const merged = normalizeProductBasePricing(res.data?.pricing);
                setServer(merged);
                setDraft(merged);
            } catch (err: any) {
                if (!cancelled) setError(err?.message || 'Erreur lors du chargement');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLiassesLoading(true);
        getAllAdminLiasses()
            .then((results) => {
                if (cancelled) return;
                setLiasses(results);
            })
            .catch(() => !cancelled && setLiasses([]))
            .finally(() => !cancelled && setLiassesLoading(false));
        return () => {
            cancelled = true;
        };
    }, []);

    const isDirty = useMemo(() => JSON.stringify(server) !== JSON.stringify(draft), [server, draft]);

    const setShowPrices = useCallback((value: boolean) => {
        setDraft((prev) => ({ ...prev, showPrices: value }));
    }, []);

    const setSectionPrice = useCallback((sectionKey: string, raw: string) => {
        const value = parsePrice(raw);
        setDraft((prev) => {
            const sections = { ...prev.sections };
            if (value === undefined) delete sections[sectionKey];
            else sections[sectionKey] = value;
            return { ...prev, sections };
        });
    }, []);

    const setItemPrice = useCallback((sectionKey: string, itemId: string, raw: string) => {
        const value = parsePrice(raw);
        setDraft((prev) => {
            const items = { ...prev.items };
            const sectionMap = { ...(items[sectionKey] || {}) };
            if (value === undefined) delete sectionMap[itemId];
            else sectionMap[itemId] = value;
            if (Object.keys(sectionMap).length === 0) delete items[sectionKey];
            else items[sectionKey] = sectionMap;
            return { ...prev, items };
        });
    }, []);

    const setButtonCategoryPrice = useCallback((sectionKey: string, category: CatalogCategory, raw: string) => {
        const value = parsePrice(raw);
        setDraft((prev) => {
            const buttonCategories = { ...prev.buttonCategories };
            const key = buttonCategoryPriceKey(sectionKey, category.id);
            if (value === undefined) delete buttonCategories[key];
            else buttonCategories[key] = value;
            return { ...prev, buttonCategories };
        });
    }, []);

    const setFabricGroupPrice = useCallback((namespace: string, raw: string) => {
        const value = parsePrice(raw);
        setDraft((prev) => {
            const fabricGroups = { ...prev.fabricGroups };
            if (value === undefined) delete fabricGroups[namespace];
            else fabricGroups[namespace] = value;
            return { ...prev, fabricGroups };
        });
    }, []);

    const setFabricPrice = useCallback((tissuId: string, raw: string) => {
        const value = parsePrice(raw);
        setDraft((prev) => {
            const fabrics = { ...prev.fabrics };
            if (value === undefined) delete fabrics[tissuId];
            else fabrics[tissuId] = value;
            return { ...prev, fabrics };
        });
    }, []);

    const toggleSection = useCallback((sectionKey: string) => {
        setExpandedSection((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    }, []);

    const toggleButtonCategory = useCallback((categoryId: string) => {
        setExpandedButtonCategory((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
    }, []);

    const toggleLiasse = useCallback((namespace: string) => {
        setExpandedLiasse((prev) => ({ ...prev, [namespace]: !prev[namespace] }));
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        setMessage(null);
        setError(null);
        try {
            const res = await updateAdminPricing({
                ...draft,
                sections: mirrorVestValues(draft.sections),
                items: mirrorVestItems(draft.items),
                buttonCategories: mirrorVestCategories(draft.buttonCategories),
            });
            const next = normalizeProductBasePricing(res.data?.pricing);
            setServer(next);
            setDraft(next);
            setMessage('Tarification enregistrée');
        } catch (err: any) {
            setError(err?.message || 'Erreur lors de l\u2019enregistrement');
        } finally {
            setSaving(false);
        }
    }, [draft]);

    const handleReset = useCallback(() => {
        setDraft(server);
        setMessage(null);
        setError(null);
    }, [server]);

    const currencySymbol = draft.currency === 'EUR' ? '€' : draft.currency;

    return (
        <Layout>
            <div className="h-full overflow-y-auto">
                <div className="max-w-3xl mx-auto px-6 py-10">
                    <h1 className="text-2xl font-semibold text-gray-900 mb-1">Tarification (prix de gros)</h1>
                    <p className="text-sm text-gray-500 mb-6">
                        Ces prix correspondent à ce que les membres revendeurs vous paient pour chaque option.
                        Ils ne sont pas visibles côté client final ; les membres définissent leurs propres prix
                        de revente.
                    </p>

                    {message && (
                        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-emerald-50 text-emerald-700 rounded-xl text-sm">
                            <CheckCircleIcon className="w-5 h-5" />
                            {message}
                        </div>
                    )}
                    {error && (
                        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-50 text-red-700 rounded-xl text-sm">
                            <XCircleIcon className="w-5 h-5" />
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <p className="text-sm text-gray-400">Chargement…</p>
                    ) : (
                        <>
                            {/* Toggle global (purement informatif côté admin). */}
                            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 mb-6 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-[#047857]/10 flex items-center justify-center flex-shrink-0">
                                    <CurrencyEuroIcon className="w-5 h-5 text-[#047857]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">Tarification active</p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Ces prix sont saisis hors taxes. Une TVA de 20 % est ajoutée au total
                                        payé par le membre lors de chaque commande.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={draft.showPrices}
                                    onClick={() => setShowPrices(!draft.showPrices)}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${draft.showPrices ? 'bg-[#047857]' : 'bg-gray-300'}`}
                                >
                                    <span
                                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${draft.showPrices ? 'translate-x-5' : 'translate-x-0.5'}`}
                                    />
                                </button>
                            </div>

                            <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                                <label htmlFor="admin-dream-box-price" className="block text-sm font-medium text-gray-900">Prix de la DREAM BOX</label>
                                <p className="mt-1 text-xs text-gray-500">Prix de gros hors taxes facturé au membre pour chaque DREAM BOX.</p>
                                <div className="relative mt-3 w-36">
                                    <input id="admin-dream-box-price" type="number" min={0} step="0.01" value={draft.dreamBoxPrice}
                                        onChange={(event) => {
                                            const price = parsePrice(event.target.value);
                                            setDraft((previous) => ({ ...previous, dreamBoxPrice: price ?? 0 }));
                                        }}
                                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 pr-8 text-sm text-gray-900 focus:border-[#047857] focus:outline-none" />
                                    <span className="absolute right-3 top-2 text-sm text-gray-400">{currencySymbol}</span>
                                </div>
                            </div>

                            {/* Prix de base par produit */}
                            <div className="mb-5">
                                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
                                    Prix de base par produit
                                </h2>
                                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
                                    <p className="text-xs text-gray-500 mb-4">
                                        Chaque article du panier utilise son propre prix de base. Le prix du gilet
                                        vendu seul est indépendant du supplément gilet d’un costume 3 pièces.
                                        Les personnalisations et tissus ci-dessous s’ajoutent ensuite à l’article concerné.
                                    </p>
                                    <div className="divide-y divide-gray-100">
                                        {PRODUCT_BASE_PRICES.map((product) => {
                                            const suitTypeItems = product.key === SUIT_PRODUCT_KEY
                                                ? (sectionItems[SUIT_TYPE_PRICING_KEY] || [])
                                                : [];
                                            return (
                                            <div key={product.key} className="py-3 first:pt-0 last:pb-0">
                                                <div className="flex items-center gap-3">
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-gray-900">{product.label}</p>
                                                        <p className="mt-0.5 text-xs text-gray-400">{product.description}</p>
                                                    </div>
                                                    <div className="relative flex-shrink-0">
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step="0.01"
                                                            aria-label={`Prix de base — ${product.label}`}
                                                            placeholder="0"
                                                            value={formatPrice(draft.sections[product.key])}
                                                            onChange={(event) => setSectionPrice(product.key, event.target.value)}
                                                            className="w-32 pl-3 pr-8 py-2 text-sm bg-white text-gray-900 border border-gray-200 rounded-lg focus:border-[#047857] focus:outline-none"
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{currencySymbol}</span>
                                                    </div>
                                                </div>
                                                {suitTypeItems.length > 0 && (
                                                    <div className="mt-3 ml-4 border-l-2 border-gray-100 pl-4 space-y-2">
                                                        <p className="text-[11px] text-gray-400">Prix complet spécifique facultatif :</p>
                                                        {suitTypeItems.map((item) => {
                                                            const label = item.labelKey ? labelItem(item.labelKey, item.label) : (item.label || item.id);
                                                            return (
                                                                <div key={item.id} className="flex items-center gap-3">
                                                                    <span className="flex-1 min-w-0 text-xs text-gray-700 truncate">{label}</span>
                                                                    <div className="relative flex-shrink-0">
                                                                        <input
                                                                            type="number"
                                                                            min={0}
                                                                            step="0.01"
                                                                            aria-label={`Prix spécifique — ${label}`}
                                                                            placeholder={draft.sections[product.key] !== undefined ? String(draft.sections[product.key]) : '—'}
                                                                            value={formatPrice(draft.items[SUIT_TYPE_PRICING_KEY]?.[item.id])}
                                                                            onChange={(event) => setItemPrice(SUIT_TYPE_PRICING_KEY, item.id, event.target.value)}
                                                                            className="w-28 pl-2 pr-7 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-900 focus:border-[#047857] focus:outline-none"
                                                                        />
                                                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{currencySymbol}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Suppléments par section */}
                            {ADMIN_SECTION_GROUPS.map((group) => {
                                const groupSections = group.sections.filter((section) => (
                                    !PRODUCT_BASE_PRICE_KEYS.has(section.key)
                                    && section.key !== SUIT_TYPE_PRICING_KEY
                                ));
                                if (groupSections.length === 0) return null;
                                return (
                                    <div key={group.id} className="mb-5">
                                        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
                                            Suppléments — {group.id === 'standaloneVestConfiguration' ? 'Gilet (costume 3 pièces et gilet seul)' : labelSection(group.titleKey)}
                                        </h2>
                                        <ul className="bg-white border border-gray-200 rounded-2xl shadow-sm divide-y divide-gray-100 overflow-hidden">
                                            {groupSections.map((section) => {
                                                const items = sectionItems[section.key] || [];
                                                const isOpen = !!expandedSection[section.key];
                                                const sectionPrice = draft.sections[section.key];
                                                const customItemsCount = Object.keys(draft.items[section.key] || {}).length;
                                                const customButtonCategoriesCount = isCatalogSection(section.key)
                                                    ? catalogForSection(section.key).filter((category) => typeof draft.buttonCategories[buttonCategoryPriceKey(section.key, category.id)] === 'number').length
                                                    : 0;
                                                return (
                                                    <li key={section.key}>
                                                        <div className="flex items-center gap-3 px-5 py-3.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => items.length > 0 && toggleSection(section.key)}
                                                                disabled={items.length === 0}
                                                                className={`flex-1 min-w-0 text-left ${items.length > 0 ? 'cursor-pointer' : 'cursor-default'}`}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-sm font-medium text-gray-900">
                                                                        {section.key === 'coupe.addVest' ? 'Coupe du gilet' : labelSection(section.titleKey)}
                                                                    </p>
                                                                    {section.key === 'perso.embroidery' && <p className="text-xs text-gray-400">Tarif par pièce brodée : × 1, × 2 ou × 3 selon la sélection.</p>}
                                                                    {(customItemsCount + customButtonCategoriesCount) > 0 && (
                                                                        <span className="text-[11px] text-[#047857] px-1.5 py-0.5 bg-[#047857]/10 rounded-md">
                                                                            {customItemsCount + customButtonCategoriesCount} tarif(s) personnalisé(s)
                                                                        </span>
                                                                    )}
                                                                    {items.length > 0 && (
                                                                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                                                    )}
                                                                </div>
                                                            </button>
                                                            <div className="relative flex-shrink-0">
                                                                <input
                                                                    type="number"
                                                                    min={0}
                                                                    step="0.01"
                                                                    placeholder="0"
                                                                    value={formatPrice(sectionPrice)}
                                                                    onChange={(e) => setSectionPrice(section.key, e.target.value)}
                                                                    className="w-28 pl-3 pr-8 py-2 text-sm bg-white text-gray-900 border border-gray-200 rounded-lg focus:border-[#047857] focus:outline-none"
                                                                />
                                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{currencySymbol}</span>
                                                            </div>
                                                        </div>
                                                        {isOpen && items.length > 0 && (
                                                            <div className="bg-gray-50/50 border-t border-gray-100 px-5 py-3">
                                                                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                                                    {isCatalogSection(section.key) ? 'Catégories et références' : 'Items'}
                                                                </p>
                                                                {isCatalogSection(section.key) ? (
                                                                    <div className="space-y-3">
                                                                        {(isButtonSection(section.key) || section.key === COLLAR_FELT_SECTION_KEY || CONTRAST_SECTION_KEYS.includes(section.key)) && <div className="rounded-lg border border-gray-200 bg-white p-3">
                                                                            <div className="mb-2 flex items-center justify-between gap-3">
                                                                                <div>
                                                                                    <p className="text-xs font-semibold text-gray-800">{CONTRAST_SECTION_KEYS.includes(section.key) ? 'Positions de contraste' : 'Options générales'}</p>
                                                                                    {!CONTRAST_SECTION_KEYS.includes(section.key) && <p className="text-[10px] text-gray-400">Le bouton identique au costume reste sans supplément.</p>}
                                                                                </div>
                                                                            </div>
                                                                            {items.filter((item) => item.id === 'recouvert' || (section.key === COLLAR_FELT_SECTION_KEY && (item.id === 'match' || item.id === 'other')) || (CONTRAST_SECTION_KEYS.includes(section.key) && SECTION_ITEMS[section.key]?.some((position) => position.id === item.id))).map((item) => {
                                                                                const itemPrice = draft.items[section.key]?.[item.id];
                                                                                return (
                                                                                    <div key={item.id} className="flex items-center gap-3">
                                                                                        <span className="min-w-0 flex-1 truncate text-xs text-gray-700">{item.label || item.id}</span>
                                                                                        <div className="relative flex-shrink-0">
                                                                                            <input
                                                                                                type="number"
                                                                                                min={0}
                                                                                                step="0.01"
                                                                                                placeholder={sectionPrice !== undefined ? String(sectionPrice) : '—'}
                                                                                                value={formatPrice(itemPrice)}
                                                                                                onChange={(e) => setItemPrice(section.key, item.id, e.target.value)}
                                                                                                className="w-24 rounded-md border border-gray-200 bg-white py-1.5 pl-2 pr-7 text-xs text-gray-900 focus:border-[#047857] focus:outline-none"
                                                                                            />
                                                                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{currencySymbol}</span>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>}

                                                                        {catalogForSection(section.key).map((category) => {
                                                                            const categoryPrices = draft.items[section.key];
                                                                            const categoryKey = buttonCategoryPriceKey(section.key, category.id);
                                                                            const categoryPrice = draft.buttonCategories[categoryKey];
                                                                            const assignedCount = category.options.filter((option) => typeof categoryPrices?.[option.id] === 'number').length;
                                                                            const categoryOpen = !!expandedButtonCategory[categoryKey];
                                                                            return (
                                                                                <div key={category.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                                                                                    <div className="flex items-center gap-3 px-3 py-2.5">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => toggleButtonCategory(categoryKey)}
                                                                                            className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                                                                        >
                                                                                            <span className="text-xs font-semibold text-gray-800">{labelCatalogCategory(category)}</span>
                                                                                            <span className="text-[10px] text-gray-400">{assignedCount}/{category.options.length} personnalisés</span>
                                                                                            <ChevronDownIcon className={`h-3.5 w-3.5 text-gray-400 transition-transform ${categoryOpen ? 'rotate-180' : ''}`} />
                                                                                        </button>
                                                                                        <div className="relative flex-shrink-0">
                                                                                            <input
                                                                                                type="number"
                                                                                                min={0}
                                                                                                step="0.01"
                                                                                                aria-label={`Tarif de la catégorie ${labelCatalogCategory(category)}`}
                                                                                                title="Tarif par défaut de toutes les références de cette catégorie"
                                                                                                placeholder={CONTRAST_SECTION_KEYS.includes(section.key) ? '0' : sectionPrice !== undefined ? String(sectionPrice) : '0'}
                                                                                                value={formatPrice(categoryPrice)}
                                                                                                onChange={(e) => setButtonCategoryPrice(section.key, category, e.target.value)}
                                                                                                className="w-24 rounded-md border border-gray-200 bg-white py-1.5 pl-2 pr-7 text-xs text-gray-900 focus:border-[#047857] focus:outline-none"
                                                                                            />
                                                                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{currencySymbol}</span>
                                                                                        </div>
                                                                                    </div>
                                                                                    {categoryOpen && (
                                                                                        <ul className="grid grid-cols-1 gap-2 border-t border-gray-100 bg-gray-50/60 p-3 sm:grid-cols-2">
                                                                                            {category.options.map((option) => {
                                                                                                const itemPrice = categoryPrices?.[option.id];
                                                                                                return (
                                                                                                    <li key={option.id} className="flex items-center gap-2 rounded-lg bg-white p-2">
                                                                                                        <img src={option.image} alt="" loading="lazy" className="h-9 w-9 rounded-md border border-gray-100 object-cover" />
                                                                                                        <span className="min-w-0 flex-1 truncate text-xs text-gray-700" title={option.name}>{option.name}</span>
                                                                                                        <div className="relative flex-shrink-0">
                                                                                                            <input
                                                                                                                type="number"
                                                                                                                min={0}
                                                                                                                step="0.01"
                                                                                                                aria-label={`Tarif de la référence ${option.name}`}
                                                                                                                placeholder={categoryPrice !== undefined ? String(categoryPrice) : (sectionPrice !== undefined ? String(sectionPrice) : '—')}
                                                                                                                value={formatPrice(itemPrice)}
                                                                                                                onChange={(e) => setItemPrice(section.key, option.id, e.target.value)}
                                                                                                                className="w-20 rounded-md border border-gray-200 bg-white py-1.5 pl-2 pr-6 text-xs text-gray-900 focus:border-[#047857] focus:outline-none"
                                                                                                            />
                                                                                                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{currencySymbol}</span>
                                                                                                        </div>
                                                                                                    </li>
                                                                                                );
                                                                                            })}
                                                                                        </ul>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <ul className="space-y-1.5">
                                                                        {items.map((item) => {
                                                                            const label = item.labelKey ? labelItem(item.labelKey, item.label) : (item.label || item.id);
                                                                            const itemPrice = draft.items[section.key]?.[item.id];
                                                                            return (
                                                                                <li key={item.id} className="flex items-center gap-3">
                                                                                    <span className="flex-1 min-w-0 text-xs text-gray-700 truncate">{label}</span>
                                                                                    <div className="relative flex-shrink-0">
                                                                                        <input
                                                                                            type="number"
                                                                                            min={0}
                                                                                            step="0.01"
                                                                                            placeholder={sectionPrice !== undefined ? String(sectionPrice) : '—'}
                                                                                            value={formatPrice(itemPrice)}
                                                                                            onChange={(e) => setItemPrice(section.key, item.id, e.target.value)}
                                                                                            className="w-24 pl-2 pr-7 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-900 focus:border-[#047857] focus:outline-none"
                                                                                        />
                                                                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{currencySymbol}</span>
                                                                                    </div>
                                                                                </li>
                                                                            );
                                                                        })}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}

                            {/* Tissus / liasses */}
                            <div className="mb-5">
                                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
                                    Tissus
                                </h2>
                                {liassesLoading ? (
                                    <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 text-sm text-gray-400">
                                        Chargement des tissus…
                                    </div>
                                ) : (
                                    <ul className="bg-white border border-gray-200 rounded-2xl shadow-sm divide-y divide-gray-100 overflow-hidden">
                                        {liasses.map((liasse) => {
                                            const isOpen = !!expandedLiasse[liasse.namespace];
                                            const groupPrice = draft.fabricGroups[liasse.namespace];
                                            const customFabricsCount = liasse.items.filter((f) => draft.fabrics[f.id] !== undefined).length;
                                            const label = liasse.namespaceLabel;
                                            return (
                                                <li key={liasse.namespace}>
                                                    <div className="flex items-center gap-3 px-5 py-3.5">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleLiasse(liasse.namespace)}
                                                            className="flex-1 min-w-0 text-left cursor-pointer"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-sm font-medium text-gray-900">{label}</p>
                                                                {customFabricsCount > 0 && (
                                                                    <span className="text-[11px] text-[#047857] px-1.5 py-0.5 bg-[#047857]/10 rounded-md">
                                                                        {customFabricsCount} tissu(s) personnalisé(s)
                                                                    </span>
                                                                )}
                                                                <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                                            </div>
                                                            <p className="text-xs text-gray-400 mt-0.5">{liasse.items.length} tissus</p>
                                                        </button>
                                                        <div className="relative flex-shrink-0">
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                step="0.01"
                                                                placeholder="0"
                                                                value={formatPrice(groupPrice)}
                                                                onChange={(e) => setFabricGroupPrice(liasse.namespace, e.target.value)}
                                                                className="w-28 pl-3 pr-8 py-2 text-sm bg-white text-gray-900 border border-gray-200 rounded-lg focus:border-[#047857] focus:outline-none"
                                                            />
                                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{currencySymbol}</span>
                                                        </div>
                                                    </div>
                                                    {isOpen && (
                                                        <div className="bg-gray-50/50 border-t border-gray-100 px-5 py-3">
                                                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                                                Tissus
                                                            </p>
                                                            <ul className="space-y-1.5">
                                                                {liasse.items.map((fabric) => {
                                                                    const fabricPrice = draft.fabrics[fabric.id];
                                                                    return (
                                                                        <li key={fabric.id} className="flex items-center gap-3">
                                                                            <span className="flex-1 min-w-0 text-xs text-gray-700 truncate">{fabric.id}</span>
                                                                            <div className="relative flex-shrink-0">
                                                                                <input
                                                                                    type="number"
                                                                                    min={0}
                                                                                    step="0.01"
                                                                                    placeholder={groupPrice !== undefined ? String(groupPrice) : '—'}
                                                                                    value={formatPrice(fabricPrice)}
                                                                                    onChange={(e) => setFabricPrice(fabric.id, e.target.value)}
                                                                                    className="w-24 pl-2 pr-7 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-900 focus:border-[#047857] focus:outline-none"
                                                                                />
                                                                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{currencySymbol}</span>
                                                                            </div>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 mt-8 sticky bottom-4 bg-white/90 backdrop-blur p-3 rounded-xl border border-gray-200 shadow-lg">
                        <button
                            type="button"
                            onClick={handleReset}
                            disabled={!isDirty || saving || loading}
                            className="flex-1 py-2.5 border border-gray-200 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!isDirty || saving || loading}
                            className="flex-1 py-2.5 bg-[#047857] text-white text-sm font-medium rounded-xl hover:bg-[#035C43] transition-colors disabled:opacity-50"
                        >
                            {saving ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
